import { Component } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';

type PageKey =
  | 'general'
  | 'catalog'
  | 'documents'
  | 'starlink-order'
  | 'ihs-order';

@Component({
  selector: 'app-starlink-flow',
  templateUrl: './starlink-flow.component.html',
  styleUrl: './starlink-flow.component.css',
})
export class StarlinkFlowComponent {
  currentPage: PageKey = 'general';
  pageHistory: PageKey[] = [];
  fullData: { [key: string]: any } = {};

  readonly orderedSteps: PageKey[] = [
    'general',
    'catalog',
    'documents',
    'starlink-order',
    'ihs-order',
  ];

  readonly stepLabels: Record<PageKey, string> = {
    general: 'General Info',
    catalog: 'Catalog',
    documents: 'Documents',
    'starlink-order': 'Starlink Order',
    'ihs-order': 'IHS Order',
  };

  constructor(
    private message: NzMessageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      console.log(params);
      const customerInfoParam = params['customerInfo'];
      const customerId = params['customerId'];
      if (customerInfoParam && customerId) {
        try {
          this.fullData['customerInfo'] = JSON.parse(customerInfoParam);
          this.fullData['customerId'] = customerId;
          if(params['catalog']) {
            this.fullData['catalog'] = { reservedSku: { sku: JSON.parse(params['catalog']) }  };
          }
          if(params['starlinkOrder']){
            this.fullData['starlinkOrder'] = JSON.parse(params['starlinkOrder']);
          }
          if(params['workOrderId'])
            this.fullData['workOrderId'] = JSON.parse(params['workOrderId']);
          this.fullData = { ...this.fullData };
          this.fullData['replayedLead'] = true;
          this.currentPage = 'general';
        } catch (e) {
          console.error('Failed to parse replay customerInfo', e);
        }
      } else {
        this.fullData['replayedLead'] = false;
      }
    });
  }

  get currentStepLabel(): string {
    return this.stepLabels[this.currentPage] ?? 'Starlink Flow';
  }

  get currentStepIndex(): number {
    return this.orderedSteps.indexOf(this.currentPage);
  }

  get progressPercent(): number {
    const stepsCount = this.orderedSteps.length;
    if (stepsCount === 0) return 0;
    const position = this.currentStepIndex;
    const clampedPosition = Math.max(0, Math.min(position, stepsCount));
    return Math.round((clampedPosition / stepsCount) * 100);
  }

  get hasCustomerInfo(): boolean {
    return !!this.fullData?.['customerInfo'];
  }

  get customerInfo(): any {
    return this.fullData?.['customerInfo'] || {};
  }

  copyField(value: string, fieldName: string): void {
    if (!value) {
      this.message.warning(`${fieldName} is empty`);
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          this.message.success(`${fieldName} copied to clipboard`);
        })
        .catch(() => {
          this.fallbackCopy(value);
        });
    } else {
      this.fallbackCopy(value);
    }
  }

  private fallbackCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      this.message.success('Copied to clipboard');
    } catch (err) {
      this.message.error('Failed to copy. Please copy manually.');
    }
    document.body.removeChild(textarea);
  }

  isStepComplete(step: PageKey): boolean {
    return this.orderedSteps.indexOf(step) < this.currentStepIndex;
  }

  isStepActive(step: PageKey): boolean {
    return step === this.currentPage;
  }

  goBack(): void {
    const prev = this.pageHistory.pop();
    if (prev) {
      this.currentPage = prev;
    }
  }

  setPage(next: PageKey): void {
    this.pageHistory.push(this.currentPage);
    this.currentPage = next;
  }

  route(e: any): void {
    this.fullData = {
      ...this.fullData,
      ...e,
    };
    this.setPage(e.nextPage);
  }

  handleCatalogDone(e: { reservedSku: any; nextPage: 'documents', workOrderId: string }): void {
    this.fullData = {
      ...this.fullData,
      catalog: { reservedSku: e.reservedSku },
      workOrderId: e.workOrderId
    };
    this.setPage(e.nextPage);
  }

  handleDocumentsDone(e: {
    acknowledged: boolean;
    nextPage: 'starlink-order';
  }): void {
    this.fullData = {
      ...this.fullData,
      documents: { acknowledged: e.acknowledged },
    };
    this.setPage(e.nextPage);
  }

  handleStarlinkOrderDone(e: {
    starlinkOrderNumber: string;
    nextPage: 'ihs-order';
    workOrderId: string;
  }): void {
    this.fullData = {
      ...this.fullData,
      starlinkOrder: { starlinkOrderNumber: e.starlinkOrderNumber },
      // workOrderId: e.workOrderId,
    };
    this.setPage(e.nextPage);
  }

  handleIhsOrderDone(e: { ihsOrderNumber: string }): void {
    this.fullData = {
      ...this.fullData,
      ihsOrder: { ihsOrderNumber: e.ihsOrderNumber },
    };
    // Final save – TODO: call backend to transition SKU to completed, persist to DB
    // console.log('[Starlink Flow] Complete – fullData:', this.fullData);
    this.resetToGeneral();
    // Optionally: this.router.navigate(['/some-confirmation']); or show inline success
  }

  resetToGeneral(): void {
    this.fullData = {};
    this.pageHistory = [];
    // this.currentPage = 'general';
    this.router.navigate(['/home']);
  }
}
